import theme from 'theme'
import { TrafficImpactMapping } from 'types/api/attacks/details/create'
import { InfraAttack } from 'types/api/attacks/details/infra-attack'
import {
  AttackCliArgs,
  AttackConfiguration
} from 'types/api/attacks/details/targeting'
import { K8sAttack, K8sSubAttack } from 'types/api/kubernetes/attacks'

const DOT_COLOR = {
  SUCCESSFUL: theme.colors.green[0],
  RESOLVING: theme.colors.blue[0],
  LAUNCHED: theme.colors.blue[0],
  // UserHalted: theme.colors.steel[1], Halting is handled by the ILFI stage utils
  ERROR: theme.colors.red[0]
}

export function isKubernetesStage(stage) {
  return Object.keys(DOT_COLOR).includes(stage)
}

export function getK8AttackStageColor(stage) {
  return DOT_COLOR[stage]
}

export function isKubernetesAttackActive(k8Attack) {
  return k8Attack.stage === 'RESOLVING'
}

/**
 * Detect if an attack originates from k8
 *
 * Basic shape of a k8 attack
 * {
    "uid": "cc7202eb-1b80-48c2-b202-eb1b8008c27c",
    "attack": {},
    "state": "UNRESOLVED",
    "createdAt": "2019-10-31T22:02:25.811Z",
    "updatedAt": "2019-10-31T22:02:25.811Z"
  }
 */

export function isKubernetesAttack(k8Attack: K8sAttack) {
  return Boolean(k8Attack.uid && k8Attack.attack)
}

export type FormattedIlfiAttack = Partial<InfraAttack> & {
  attack: K8sSubAttack
  attackConfiguration: AttackConfiguration
  trafficImpactMapping: TrafficImpactMapping
  uid: string
}

export function formatK8AttackIntoIlfiAttack(
  k8Attack: K8sAttack = {} as K8sAttack
): FormattedIlfiAttack {
  const defaultCliArgs: AttackCliArgs = [] as unknown as AttackCliArgs
  const cliArgs: AttackCliArgs =
    k8Attack.attack?.impactDefinition?.cliArgs ?? defaultCliArgs

  const containerSelection =
    k8Attack.attack?.targetDefinition?.strategy?.containerSelection

  return {
    args: cliArgs,
    attack: k8Attack.attack,
    attackConfiguration: k8Attack.attackConfiguration,
    create_user: k8Attack.createUser,
    error: k8Attack.error,
    guid: k8Attack.uid,
    infra_command: {
      commandType: cliArgs[0],
      commandArgs: cliArgs
    },
    kind: 'WebApp',
    providers: k8Attack.attack?.impactDefinition?.providers ?? [],
    stage: k8Attack.stage,
    start_time: k8Attack.createdAt,
    target_type: 'Kubernetes',
    targets: (
      k8Attack.attack?.targetDefinition?.strategy?.k8sObjects ?? []
    ).map((t) => t.name || t.uid),
    trafficImpactMapping:
      k8Attack.attack?.impactDefinition?.trafficImpactMapping,
    uid: k8Attack.uid,
    containerSelection
  }
}

export function generateKubernetesRerunLinkTo(attack) {
  if (attack.serviceId) {
    return {
      pathname: '/attacks/new/services',
      state: {
        attack: attack,
        service: { id: attack.serviceId }
      }
    }
  }

  return {
    pathname: '/attacks/new/kubernetes',
    state: { attack: attack }
  }
}
